import Chip, { ChipProps } from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

export type StatusChipTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "primary"
  | "secondary"
  | "cyan"
  | "purple"
  | "dirtyYellow"
  | "dirtyBlue"
  | "dirtyGreen"
  | "darkGray"
  | "brown"
  | "gray";

export type StatusChipProps = Omit<ChipProps, "color"> & {
  tone?: StatusChipTone;
};

const SEMANTIC_TONES = new Set([
  "success",
  "warning",
  "error",
  "info",
  "primary",
  "secondary",
]);

export function StatusChip({
  tone = "neutral",
  size = "small",
  sx,
  ...props
}: StatusChipProps) {
  const theme = useTheme();

  if (tone === "neutral") {
    return (
      <Chip
        size={size}
        sx={{
          fontWeight: 600,
          borderRadius: "6px",
          bgcolor: "grey.100",
          color: "text.secondary",
          ...sx,
        }}
        {...props}
      />
    );
  }

  if (SEMANTIC_TONES.has(tone)) {
    return (
      <Chip
        size={size}
        color={tone as ChipProps["color"]}
        sx={{ fontWeight: 600, borderRadius: "6px", ...sx }}
        {...props}
      />
    );
  }

  // Custom palette keys from ThemeManager (stage colors, etc.)
  const paletteColor = (theme.palette as any)[tone];
  const main = paletteColor?.main ?? theme.palette.grey[500];
  const contrast =
    paletteColor?.contrastText ?? theme.palette.getContrastText(main);

  return (
    <Chip
      size={size}
      sx={{
        fontWeight: 600,
        borderRadius: "6px",
        bgcolor: main,
        color: contrast,
        ...sx,
      }}
      {...props}
    />
  );
}

export default StatusChip;
